import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { getMe, login as apiLogin, register as apiRegister } from '../api';

/** Decode our simple base64 token to get role */
function decodeToken(token: string): { role?: string } {
  try {
    return JSON.parse(atob(token));
  } catch {
    return {};
  }
}

export interface User {
  id: string;
  username: string;
  displayName: string;
  role: 'admin' | 'user';
}

interface AuthContextType {
  user: User | null;
  token: string | null;
  loading: boolean;
  isAdmin: boolean;
  login: (username: string, password: string) => Promise<void>;
  register: (username: string, password: string, displayName?: string) => Promise<void>;
  logout: () => void;
}

const AuthContext = createContext<AuthContextType>({
  user: null,
  token: null,
  loading: true,
  isAdmin: false,
  login: async () => {},
  register: async () => {},
  logout: () => {},
});

export const useAuth = () => useContext(AuthContext);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);

  const setAuth = useCallback((t: string, u: User) => {
    localStorage.setItem('token', t);
    localStorage.setItem('user', JSON.stringify(u));
    setToken(t);
    setUser(u);
    setIsAdmin(u.role === 'admin');
  }, []);

  const clearAuth = useCallback(() => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    setToken(null);
    setUser(null);
    setIsAdmin(false);
  }, []);

  // Restore from localStorage on mount
  useEffect(() => {
    const savedToken = localStorage.getItem('token');
    const savedUser = localStorage.getItem('user');
    if (savedToken && savedUser) {
      const u: User = JSON.parse(savedUser);
      setToken(savedToken);
      setUser(u);
      setIsAdmin(u.role === 'admin');
      // Verify token is still valid
      getMe().catch(() => clearAuth());
    }
    setLoading(false);

    const handler = () => clearAuth();
    window.addEventListener('auth:logout', handler);
    return () => window.removeEventListener('auth:logout', handler);
  }, [clearAuth]);

  const login = useCallback(async (username: string, password: string) => {
    const result = await apiLogin(username, password);
    const decoded = decodeToken(result.token);
    const u: User = {
      id: result.user.id,
      username: result.user.username,
      displayName: result.user.displayName,
      role: (decoded.role as 'admin' | 'user') || 'user',
    };
    setAuth(result.token, u);
  }, [setAuth]);

  const register = useCallback(async (username: string, password: string, displayName?: string) => {
    const result = await apiRegister(username, password, displayName);
    const decoded = decodeToken(result.token);
    const u: User = {
      id: result.user.id,
      username: result.user.username,
      displayName: result.user.displayName,
      role: (decoded.role as 'admin' | 'user') || 'user',
    };
    setAuth(result.token, u);
  }, [setAuth]);

  const logout = useCallback(() => {
    clearAuth();
  }, [clearAuth]);

  return (
    <AuthContext.Provider value={{ user, token, loading, isAdmin, login, register, logout }}>
      {children}
    </AuthContext.Provider>
  );
};
